import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { BatteryWarning, BookOpen, ChevronRight, ClipboardList, LogOut, Wrench } from 'lucide-react';
import { VeoMark } from '@/components/VeoMark';
import { Alert, Starfield } from '@/ui';
import {
  TECH_ACCESS_TOKEN,
  TECH_PATHS,
  TECH_PHONE,
  TechRequestError,
  clearTechSession,
  consumeHandoffToken,
  displayName,
  formatPhone,
  getCurrentTechnician,
  greetingKey,
  techStorage,
  type TechnicianProfile,
} from './techSession';

/** The forms, as the VeoTech apps' Home page listed them. */
export const TECH_FORMS = [
  { key: 'handbook', icon: BookOpen, url: 'https://www.notion.so/veoride/Field-Tech-Resource-Library-0311dbfdeea3412d8465769cc540f229' },
  { key: 'batteryStolen', icon: BatteryWarning, url: 'https://veoride.typeform.com/to/Do3mFQuF' },
  { key: 'inFieldRepair', icon: Wrench, url: 'https://veoride.typeform.com/to/KIjO2ed1' },
  { key: 'startEndOfWork', icon: ClipboardList, url: 'https://veoride.typeform.com/to/Nu9jPfMo' },
] as const;

/** Full-height, centred column shared by the technician pages. */
export function TechScreen({ children }: { children: ReactNode }) {
  return (
    <div className="relative isolate min-h-screen bg-canvas px-4 py-6 font-sans text-ink">
      <Starfield className="-z-10" opacity={0.35} density={0.00008} />
      <main className="mx-auto w-full max-w-[440px]">{children}</main>
    </div>
  );
}

/**
 * Technician home: what the VeoTech apps' "Form Access" opens (home.veoride.com/tech), as the
 * retired Home console had it. Signed-in technicians only: the app's token (or a /tech/login
 * session) loads their profile for the greeting; without one, or when it is rejected, they sign in.
 */
export function TechFormsPage() {
  const { t } = useTranslation('fieldops', { keyPrefix: 'techForms' });
  const navigate = useNavigate();
  const [profile, setProfile] = useState<TechnicianProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    consumeHandoffToken();
    if (!techStorage.get(TECH_ACCESS_TOKEN)) {
      navigate(TECH_PATHS.login, { replace: true });
      return;
    }
    let cancelled = false;
    getCurrentTechnician()
      .then((p) => !cancelled && setProfile(p ?? null))
      .catch((e: unknown) => {
        if (cancelled) return;
        // an expired or revoked session: sign in again
        if (e instanceof TechRequestError && (e.status === 401 || e.status === 403)) {
          clearTechSession();
          navigate(TECH_PATHS.login, { replace: true });
          return;
        }
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const signOut = () => {
    clearTechSession(true);
    navigate(TECH_PATHS.login, { replace: true });
  };

  const first = displayName(profile).split(/\s+/)[0] ?? '';
  const greeting = t(`greeting.${greetingKey(new Date().getHours())}`);
  const phone = formatPhone(profile?.phone ?? techStorage.get(TECH_PHONE));
  const subtitle = loading ? t('loadingProfile') : profile?.areaName || profile?.email || phone || t('signedIn');

  return (
    <TechScreen>
      <VeoMark size={36} />
      <h1 className="m-0 mt-3 font-display text-2xl font-semibold text-heading">{first ? `${greeting}, ${first}` : greeting}</h1>
      <p className="m-0 mb-5 mt-0.5 text-base text-ink-muted">{subtitle}</p>
      {error && (
        <Alert tone="danger" title={t('profileError')} className="mb-4">
          {error}
        </Alert>
      )}
      <h2 className="m-0 mb-2 text-sm font-semibold uppercase tracking-wide text-ink-muted">{t('quickActions')}</h2>
      <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
        {TECH_FORMS.map(({ key, icon: Icon, url }) => (
          <li key={key}>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-xl border border-line bg-surface p-4 text-ink no-underline shadow-sm hover:border-accent/40 hover:bg-accent-softer"
            >
              <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-accent-soft text-accent">
                <Icon className="size-5" aria-hidden />
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="text-base font-semibold">{t(`forms.${key}.label`)}</span>
                <span className="text-sm text-ink-muted">{t(`forms.${key}.hint`)}</span>
              </span>
              <ChevronRight className="size-4 shrink-0 text-ink-faint" aria-hidden />
            </a>
          </li>
        ))}
      </ul>
      <h2 className="m-0 mb-2 mt-6 text-sm font-semibold uppercase tracking-wide text-ink-muted">{t('account')}</h2>
      <button
        type="button"
        onClick={signOut}
        className="flex w-full cursor-pointer items-center gap-3 rounded-xl border border-line bg-surface p-4 text-left text-ink shadow-sm hover:bg-danger-soft"
      >
        <LogOut className="size-5 shrink-0 text-danger" aria-hidden />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-base font-semibold">{t('signOut')}</span>
          <span className="truncate text-sm text-ink-muted">{profile?.email || phone}</span>
        </span>
        <ChevronRight className="size-4 shrink-0 text-ink-faint" aria-hidden />
      </button>
    </TechScreen>
  );
}
